/* global grecaptcha */
import { useState, useEffect, useRef } from 'react';
import firebase from 'firebase/compat/app';
import { useTranslation } from 'react-i18next';
import { string, func } from 'prop-types';

// Import helpers
import {
	RECAPTCHA_CONTAINER_ID,
	RECAPTCHA_TYPE,
	AUTH_FIELDS_NAMES,
	getPhoneNumberFormInitialValues,
	UNKNOWN_DIAL_CODE
} from '../helpers';

// Import variables
import { LOCAL_STORAGE_LANGUAGE } from 'translations/helpers';
import { POST_SUBSCRIBER_LOGIN_EXTERNAL_API } from 'helpers/api';

// Import utilities
import { useAuthorization } from 'components/utilities';

const { PHONE_NUMBER, CODE } = AUTH_FIELDS_NAMES;

const usePhoneAuthentication = ({ setLoading, userCountryCode }) => {
	const { t } = useTranslation();
	const { userAuthorization } = useAuthorization({
		apiUrl: POST_SUBSCRIBER_LOGIN_EXTERNAL_API
	});

	// state
	const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(true);
	const [isRecaptchaSuccess, setIsRecaptchaSuccess] = useState(false);
	const [confirmationResult, setConfirmationResult] = useState(null);

	// refs
	const phoneNumberFormRef = useRef();
	const codeFormRef = useRef();
	const recaptchaVerifierRef = useRef();
	const widgetIdRef = useRef();

	// variables
	const phoneNumberFormInitialValues = getPhoneNumberFormInitialValues(
		userCountryCode
	);

	const recaptchaInitialization = async () => {
		// set recaptcha lang
		firebase.auth().languageCode = localStorage.getItem(LOCAL_STORAGE_LANGUAGE);
		// initialization
		const recaptcha = new firebase.auth.RecaptchaVerifier(
			RECAPTCHA_CONTAINER_ID,
			{
				size: RECAPTCHA_TYPE,
				callback: () => setIsRecaptchaSuccess(true)
			}
		);

		// get widget id
		const widgetId = await recaptcha.render();

		// set refs
		recaptchaVerifierRef.current = recaptcha;
		widgetIdRef.current = widgetId;

		// turn off loading
		setIsRecaptchaLoading(false);
	};

	const handleSetFormikError = ({ formik, fieldName, errorMessage }) => {
		// Set loading
		setLoading(false);
		// Set form submiting to false
		formik.setSubmitting(false);
		// Set form error
		formik.setErrors({ [fieldName]: errorMessage });
	};

	const handlePhoneNumberFormSubmit = async ({ dialCode, phone_number }) => {
		try {
			// Set loading
			setLoading(true);

			const { current: recaptchaWidget } = recaptchaVerifierRef;

			let phoneNumber = '';

			const isDialCodeUnkown = dialCode === UNKNOWN_DIAL_CODE;

			if (isDialCodeUnkown) {
				phoneNumber = `+${phone_number}`;
			} else {
				phoneNumber = `+${dialCode}${phone_number}`;
			}

			// SignIn with phone
			const response = await firebase
				.auth()
				.signInWithPhoneNumber(phoneNumber, recaptchaWidget);

			// Set confirmation result
			setConfirmationResult(response);

			// Turn off loading
			setLoading(false);
		} catch ({ message }) {
			const { current: formik } = phoneNumberFormRef;
			const { current: widgetId } = widgetIdRef;

			// Set formik error
			handleSetFormikError({
				formik,
				fieldName: PHONE_NUMBER,
				errorMessage: message
			});

			// Reset recaptcha
			setIsRecaptchaSuccess(false);
			grecaptcha.reset(widgetId);
		}
	};

	const handleCodeFormSubmit = async ({ code }) => {
		try {
			// Set loading
			setLoading(true);

			// send verification code
			const { user } = await confirmationResult.confirm(code);

			// get user token
			const idToken = await user.getIdToken();

			// dispatch user authorization
			await userAuthorization({ idToken });

			// Turn off loading
			setLoading(false);
		} catch ({ message }) {
			// Set formik error
			handleSetFormikError({
				formik: codeFormRef.current,
				fieldName: CODE,
				errorMessage: t('common_login_firebase_code_validation')
			});
		}
	};

	useEffect(() => {
		recaptchaInitialization();
		// eslint-disable-next-line
	}, []);

	return {
		phoneNumberFormSubmit: handlePhoneNumberFormSubmit,
		codeFormSubmit: handleCodeFormSubmit,
		isRecaptchaLoading,
		isRecaptchaSuccess,
		phoneNumberFormRef,
		codeFormRef,
		isCodeFormVisible: !!confirmationResult,
		phoneNumberFormInitialValues
	};
};

usePhoneAuthentication.propTypes = {
	setLoading: func.isRequired,
	userCountryCode: string.isRequired
};

export default usePhoneAuthentication;
